// Benchmark selector (FM-2101) — decides WHICH benchmark a participant's portfolio
// is measured against, and whether a comparison applies at all. The "vs the market"
// chart reads the finished object and re-derives nothing.
//
// Output contract:
//   {
//     applicable: boolean,                   // false ⇒ hide the comparison entirely
//     name: string|null,                     // display name (blend label / model / index)
//     type: 'blended'|'model'|'index'|null,
//     series: [{ date, value }],             // cumulative benchmark return, % per point
//     deltaPct: number|null,                 // participant return − benchmark, over the range
//     composition: [{ label, weightPct }],   // what the benchmark is made of
//     reason: 'planType'|'missingData'|null, // why it isn't applicable
//     historical: boolean,                   // plan ended → past-performance framing
//   }
//
// Rules:
//   • Model portfolio → type 'model'; the model brings its own benchmark and the
//     participant's wider asset mix is NOT blended in.
//   • Fund portfolio  → type 'blended'; each asset class's index is weighted by the
//     participant's balance share and combined into one series. A single-index
//     result degrades to type 'index'.
//   • applicable=false for cash-balance / guaranteed-rate plans and whenever the
//     index or return data for the range is missing.
//   • deltaPct = participant return − benchmark return over the range.
//
// WHERE THE DATA COMES FROM (today vs later):
//   The backend does not ship a benchmark object yet, so the blend is computed here
//   from fields /portfolio already returns — holdings balances, asset classes, the
//   per-fund fact sheets (index name + index return) and the performance series.
//   When the backend adds its own benchmark, `benchmarkMeta.composition` /
//   `.seriesByRange` are read first and used as-is — that is the only switch-over
//   point; nothing else in the UI changes.
import { formatPercent } from '../../../utils/format';

const round2 = (n) => Math.round(n * 100) / 100;

// Range → the fact-sheet compare column that carries that period's INDEX return.
// 1M/3M/YTD have no such column; those fall back to the held fund's own return
// (an index fund tracks its index closely, and it's the only per-class return the
// payload carries for those periods).
const FACT_PERIOD = { '1Y': '1Yr', '3Y': '3Yr', '5Y': '5Yr', '10Y': '10Yr' };

// Range → the per-fund return field on the UI fund model.
const FUND_RETURN = {
  '1M': 'oneMo',
  '3M': 'threeMo',
  YTD: 'ytd',
  '1Y': 'oneYr',
  '3Y': 'threeYr',
  '5Y': 'fiveYr',
  '10Y': 'tenYr',
};

// Plan types with no participant-directed market investments (matched loosely,
// case-insensitively, against whatever the payload calls the plan type).
const NO_BENCHMARK_PLAN_TYPES = ['cash balance', 'guaranteed'];

// The most `shapeSeries` may amplify an index's return when borrowing the market
// series' shape. `scale = targetPct / last` multiplies EVERY point, so a series that
// swings wide and lands near zero blows the shape apart: Acme's 3M market series peaks
// at 5.0 and ends at -0.2, which would stretch a +4% class into a line plunging past
// -100% mid-chart before landing on +4. Only that final point survives — and it's the
// only one `deltaPct` reads, so the distortion is invisible to every downstream check
// and shows up solely as a wrong-looking chart. Past this bound the endpoint carries
// too little of the series' own magnitude to scale against, so we ramp instead.
const MAX_SHAPE_AMPLIFICATION = 3;

/** Not-applicable result; `reason` lets the card pick its copy. */
const hidden = (reason, historical = false) => ({
  applicable: false,
  name: null,
  type: null,
  series: [],
  deltaPct: null,
  composition: [],
  reason,
  historical,
});

/** Names used when the caller passes no translator (tests, non-React callers). */
const defaultLabels = {
  blend: () => 'Blended benchmark',
  model: (model) => `${model} model benchmark`,
  classIndex: (label) => `${label} Index`,
};

/**
 * Group holdings into asset classes carrying a balance weight. Real balances win;
 * a plan that ships classes without per-fund balances falls back to the rollup %.
 */
const weightGroups = (funds = [], assetClasses = []) => {
  const total = funds.reduce((s, f) => s + (f.balance || 0), 0);
  if (total > 0) {
    const byKey = new Map();
    funds.forEach((f) => {
      const key = f.assetClassKey || f.assetClass || 'other';
      const group = byKey.get(key) || { key, label: f.assetClass || key, balance: 0, funds: [] };
      group.balance += f.balance || 0;
      group.funds.push(f);
      byKey.set(key, group);
    });
    return [...byKey.values()].map((g) => ({ ...g, weightPct: (g.balance / total) * 100 }));
  }
  return (assetClasses || [])
    .filter((c) => c.pct)
    .map((c) => ({
      key: c.key,
      label: c.label || c.key,
      balance: c.balance || 0,
      weightPct: c.pct,
      funds: funds.filter((f) => (c.tickers || []).includes(f.ticker)),
    }));
};

/** The index a class is measured against: its largest holding's fact-sheet benchmark. */
const groupIndexName = (group, fundDetail = {}, labels) => {
  const largest = [...group.funds].sort((a, b) => (b.balance || 0) - (a.balance || 0))[0];
  return fundDetail[largest?.ticker]?.benchmark || labels.classIndex(group.label);
};

/**
 * The class's index return for the range: balance-weighted over its funds,
 * preferring each fund's fact-sheet INDEX return and falling back to the fund's own
 * return. null when the class carries no return data for this range.
 */
const groupIndexReturn = (group, range, fundDetail = {}) => {
  const factKey = FACT_PERIOD[range];
  const fundKey = FUND_RETURN[range];
  const held = group.funds.reduce((s, f) => s + (f.balance || 0), 0);
  let weight = 0;
  let sum = 0;
  group.funds.forEach((f) => {
    const indexPct = factKey ? fundDetail[f.ticker]?.compare?.[factKey]?.ben : undefined;
    const pct = indexPct ?? (fundKey ? f[fundKey] : undefined);
    if (pct == null) return;
    const w = held > 0 ? (f.balance || 0) / held : 1 / group.funds.length;
    weight += w;
    sum += w * pct;
  });
  return weight > 0 ? sum / weight : null;
};

/**
 * One index's series over the range, index-aligned to `points` — the participant's own point
 * count for that range, the same count `compareDates[range]` labels.
 *
 * ALIGNMENT IS POSITIONAL, BY CONTRACT: neither source carries per-point dates (a provided
 * `seriesByRange[range][classKey]` is a bare value array, and the market series is positional
 * too), so a series is usable only if it covers exactly those points. One that doesn't can't be
 * placed against `compareDates` or compared with the participant's return, so it's refused here
 * rather than reinterpreted. (If the backend ever ships dated points, align on those instead and
 * this length rule becomes redundant.)
 *
 * A provided series wins when it conforms; otherwise the shape comes from the plan-level market
 * series (the only index-shaped time series in the payload), scaled so the last point equals this
 * index's own return. When that series ends flat — or too near flat to scale against without
 * amplifying it out of all recognition (see `MAX_SHAPE_AMPLIFICATION`) — there is no shape worth
 * borrowing, so the points ramp linearly to the target instead.
 */
const shapeSeries = (marketSeries, targetPct, provided, points) => {
  if (provided?.length === points) return provided.map((v) => round2(v));
  if (marketSeries?.length !== points || targetPct == null) return [];
  const last = marketSeries[marketSeries.length - 1];
  const peak = marketSeries.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
  // A NEGATIVE scale mirrors the shape instead of resizing it: every point the market
  // rose, the borrowed line falls, and vice versa — only the final point (the one `scale`
  // solves for) survives. That happens whenever the class moved opposite to the market,
  // which is ordinary: on Acme's 1M range the market ends at -3.0 while nearly every fund
  // posted a positive month, so the whole blended line was drawn as the market's mirror.
  // The magnitude bound below can't catch it — a mirrored series is perfectly in-range.
  const mirrored = Math.sign(targetPct) !== Math.sign(last);
  if (!last || mirrored || Math.abs(last) * MAX_SHAPE_AMPLIFICATION < peak) {
    const steps = marketSeries.length - 1;
    return marketSeries.map((_, i) => round2(steps ? (targetPct * i) / steps : targetPct));
  }
  const scale = targetPct / last;
  return marketSeries.map((v) => round2(v * scale));
};

// ── The two derivations below share ONE input: the usable parts, decided once by
//    `usableParts()` and handed to both. Neither filters again. That's deliberate — every
//    review finding on this file so far was the *same rule* (which classes count, at what
//    weight, over how many points) disagreeing between copies of it, so there is now exactly
//    one copy and disagreement is structurally impossible. ──

/**
 * The classes that count for a range: they carry weight, and their series spans the whole range.
 * A class with no return for the range (a fund with no 5Y history, say) has an empty series and
 * drops out here; a class holding nothing has zero weight and drops out here.
 */
const usableParts = (parts, points) =>
  parts.filter((p) => p.weightPct > 0 && p.series.length === points);

/**
 * Weighted sum of the per-index series → one blended series, exactly `points` long.
 *
 * Every part already spans the range (see `usableParts`), so there is nothing to truncate to.
 * Blending to the shortest part instead would silently shorten the benchmark line and — worse —
 * pair the participant's LATEST return with an EARLIER benchmark point when computing the gap,
 * reporting a difference between two different dates.
 */
const blendSeries = (parts, points) => {
  if (!parts.length) return [];
  const totalWeight = parts.reduce((s, p) => s + p.weightPct, 0);
  return Array.from({ length: points }, (_, i) =>
    round2(parts.reduce((s, p) => s + (p.weightPct / totalWeight) * p.series[i], 0)),
  );
};

/**
 * Merge same-named indexes, heaviest first; weights are whole percents.
 *
 * Same input as `blendSeries` — the usable parts — renormalized over their own total, so the
 * tooltip and the value it sits beside are computed off one basis. Renormalizing matters because
 * a class dropped for the range would otherwise leave the composition summing to (say) 85%
 * beside a value blended over 100%. (Whole-percent rounding can still leave the printed total a
 * point off; the basis is what matters, and the tooltip prints whole percents by design.)
 */
const mergeComposition = (parts) => {
  const totalWeight = parts.reduce((s, p) => s + p.weightPct, 0);
  if (!totalWeight) return [];
  const byLabel = new Map();
  parts.forEach((p) => byLabel.set(
    p.label,
    (byLabel.get(p.label) || 0) + (p.weightPct / totalWeight) * 100,
  ));
  return [...byLabel.entries()]
    .map(([label, weightPct]) => ({ label, weightPct: Math.round(weightPct) }))
    // A sub-0.5% sleeve carries real weight in the blend but rounds to a whole 0 here, and
    // "X Index 0%" in the tooltip reads as a broken row. `usableParts` can't pre-empt this:
    // it filters the RAW fraction, which is legitimately > 0 — only the display rounding
    // collapses it. Dropped from the list, never from `blendSeries`, which uses raw weights.
    .filter((c) => c.weightPct > 0)
    .sort((a, b) => b.weightPct - a.weightPct);
};

/**
 * Pick and build the benchmark for one compare range.
 *
 * @param {object} data the camelCase portfolio UI model (portfolio.adapters output)
 * @param {string} range '1M' | '3M' | 'YTD' | '1Y' | '3Y' | '5Y'
 * @param {{t?: function}} [options] i18n translate fn for the generated names
 */
export const selectBenchmark = (data, range, { t } = {}) => {
  const labels = t
    ? {
        blend: () => t('portfolio.benchmark.blendName'),
        model: (model) => t('portfolio.benchmark.modelName', { model }),
        classIndex: (label) => t('portfolio.benchmark.classIndex', { label }),
      }
    : defaultLabels;

  const meta = data?.benchmarkMeta || {};
  const models = meta.models || [];
  const planType = String(meta.planType || '').toLowerCase();
  const historical = ['terminated', 'inactive'].includes(
    String(meta.employmentStatus || '').toLowerCase(),
  );

  // No participant-directed market money → no benchmark. Two signals: the plan type
  // (when the payload names it) and the backend's own applicability flag. That flag
  // is also false for model-driven accounts — superseded here, since FM-2101 gives
  // those the model's own benchmark instead of hiding the comparison.
  if (NO_BENCHMARK_PLAN_TYPES.some((p) => planType.includes(p))) return hidden('planType', historical);
  if (meta.applicable === false && !models.length) return hidden('planType', historical);

  // Without the participant's own series there is nothing to compare.
  const you = data?.compare?.[range]?.you;
  if (!you?.length) return hidden('missingData', historical);
  const marketSeries = data?.compare?.[range]?.spx;
  const dates = data?.compareDates?.[range] || [];

  // A model portfolio is measured against the model's own benchmark: the indexes of
  // the funds it holds, weighted inside the model. Standalone holdings alongside it
  // don't dilute it. Several models → the largest by balance.
  const model = models.length
    ? [...models].sort((a, b) => (b.balance || 0) - (a.balance || 0))[0]
    : null;
  const funds = model
    ? (data.funds || []).filter((f) => (model.funds || []).includes(f.ticker))
    : data.funds || [];
  const groups = weightGroups(funds, model ? [] : data.assetClasses);
  if (!groups.length) return hidden('missingData', historical);

  // Every series is index-aligned to the participant's own points, so the blend spans the same
  // range and its last point is the same moment as `you`'s — which is what `deltaPct` compares.
  const points = you.length;
  const provided = meta.seriesByRange?.[range];
  const parts = usableParts(
    groups.map((g) => {
      const indexPct = groupIndexReturn(g, range, data.fundDetail);
      return {
        label: groupIndexName(g, data.fundDetail, labels),
        weightPct: g.weightPct,
        series: shapeSeries(marketSeries, indexPct, provided?.[g.key], points),
      };
    }),
    points,
  );
  if (!parts.length) return hidden('missingData', historical);

  const values = blendSeries(parts, points);
  if (!values.length) return hidden('missingData', historical);

  // A backend composition only counts if it actually has rows: `[]` is truthy, so an empty
  // per-range array would win the fallback chain, leave `composition` empty, and make the
  // `composition[0].label` below throw — during render, taking the whole Portfolio page with
  // it (both hooks call this unconditionally). Fall through to the computed blend instead.
  const providedComposition = [meta.compositionByRange?.[range], meta.composition]
    .find((c) => c?.length);
  const composition = providedComposition || mergeComposition(parts);
  // Nothing left to name the benchmark after (only reachable if every part is zero-weight).
  if (!composition.length) return hidden('missingData', historical);
  const type = model ? 'model' : composition.length > 1 ? 'blended' : 'index';
  const name = model
    ? labels.model(model.name)
    : type === 'index'
      ? composition[0].label
      : labels.blend();

  // `you.length` above proves the series EXISTS, not that its latest point has posted. A
  // trailing null is ordinary live data, and `null - 7.05` is -7.05 in JS — so without this
  // guard the contract's `number|null` silently became the NEGATED BENCHMARK, dressed up as
  // the participant's gap. Every shipped blend is positive, so it always came out as "trails
  // by <the benchmark's own return>" — a large, plausible-looking figure rather than an
  // obviously broken one. Nothing renders it today (both cards null-check first), so the
  // guard is here, at the contract boundary, rather than in a third consumer.
  const lastYou = you[you.length - 1];
  return {
    applicable: true,
    name,
    type,
    series: values.map((value, i) => ({ date: dates[i] ?? null, value })),
    deltaPct: lastYou == null ? null : round2(lastYou - values[values.length - 1]),
    composition,
    reason: null,
    historical,
  };
};

/**
 * Ahead / behind / tracking, for a gap in percentage points.
 *
 * ONE definition, shared by every card that renders a verdict — OverviewStats and
 * CompareSection previously each had their own and disagreed at the boundary: one
 * thresholded a gap it had already rounded to tenths, the other the raw two-decimal
 * value, so a -0.048 gap read "tracking closely" on one card and "trails by 0.1%" on the
 * other, on the same page, off the same selector result.
 *
 * The cut-off is where the printed figure stops carrying a direction: both cards render
 * `Math.abs(gap).toFixed(1)`, so anything under 0.05 prints as "0.0%" and claiming ahead
 * or behind at that point asserts what the number doesn't say. Classify the RAW gap —
 * pre-rounding moves the boundary and re-introduces the split this helper exists to close.
 */
export const TRACKING_EPSILON = 0.05;

export const benchmarkVerdict = (gap) => {
  if (gap == null) return null;
  if (Math.abs(gap) < TRACKING_EPSILON) return 'tracking';
  return gap < 0 ? 'behind' : 'ahead';
};

/**
 * Whether this plan's growth is CREDITED at a guaranteed rate rather than earned in the
 * market — a cash-balance / guaranteed-rate benefit. It decides the label on the YTD
 * figure ("YTD account change", not "growth"): calling credited money "growth" claims the
 * participant's investments produced it.
 *
 * `reason` is only ever set on a not-applicable result (see `hidden()`), so 'planType'
 * identifies the case on its own. `historical` is excluded deliberately: an ENDED plan is
 * showing past performance, and what it earned back then was earned.
 */
export const isCreditedRate = (benchmark) =>
  !!benchmark && !benchmark.historical && benchmark.reason === 'planType';

/** Compare ranges in display order. */
export const RANGES = ['1M', '3M', 'YTD', '1Y', '3Y', '5Y'];

/** Those a plan actually carries — keeps the period filter and the selector in step. */
export const availableRanges = (data) => RANGES.filter((r) => data?.compare?.[r]?.you?.length);

/** Composition as plain "Index 40% · Index 30%" text (aria labels, logs). */
export const compositionSummary = (composition = []) =>
  composition.map((c) => `${c.label} ${formatPercent(c.weightPct, { digits: 0 })}`).join(' · ');

export default selectBenchmark;
